/**
 * Copyright 2023-2024 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import { MutableRefObject, useEffect, useState } from "react";
import { useApplicationsPaginatedByApplicationNameLazyQuery } from "~/generated/graphql";
import { getFirstItem, isNotEmpty } from "~/tools";
import { formatDataObject } from "~/utils";
import { generateSelectedEnvs } from "../AppEnvSelectorPaginated/utils";
import { ApplicationOption, EnvironmentOption } from "../types";

type Props = {
  elementRef: MutableRefObject<null>;
  selectedEnvironments: string[];
  selectedApplications: string[];
  applicationName: string;
  isCleared: boolean;
};

export function useInfiniteApplications({
  elementRef,
  selectedEnvironments,
  selectedApplications,
  applicationName,
  isCleared,
}: Props) {
  const pageSize = 20;
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  /* 
  selectedAppEnvs was introduced to support old functionality 
  of filters when only application query parameter is provided
  */
  const [selectedAppEnvs, setSelectedAppEnvs] = useState<EnvironmentOption[]>(
    []
  );
  const [applications, setApplications] = useState<ApplicationOption[]>([]);
  const [selectedEnvs, setSelectedEnvs] = useState<EnvironmentOption[]>(
    generateSelectedEnvs(selectedEnvironments)
  );

  const [getApplications, { data, loading: isAppsLoading }] =
    useApplicationsPaginatedByApplicationNameLazyQuery({
      variables: {
        limit: pageSize,
        page,
        applicationName,
      },
    });

  useEffect(() => {
    if (isCleared) {
      setSelectedEnvs(generateSelectedEnvs(selectedEnvironments));
    }
  }, [isCleared]);

  const fetchData = async () => {
    await getApplications();
    setPage((prevPage) => prevPage + 1);
    setHasMore(
      Number(data?.applicationsPaginated?.count) > applications.length
    );
  };

  useEffect(() => {
    setPage(0);
    setApplications([]);
  }, [applicationName]);

  function onIntersection(entries: IntersectionObserverEntry[]) {
    const firstEntry = getFirstItem(entries);
    if (firstEntry.isIntersecting && hasMore) {
      void fetchData();
    }
  }

  useEffect(() => {
    const observer = new IntersectionObserver(onIntersection);
    if (observer && elementRef.current) {
      observer.observe(elementRef.current);
    }

    return () => {
      if (observer) {
        observer.disconnect();
      }
    };
  }, [elementRef]);

  useEffect(() => {
    const applicationsPaginated = formatDataObject(data);
    const newApplications =
      applicationsPaginated?.applicationsPaginated?.results ?? [];
    setApplications((prevApplications) => {
      return [...prevApplications, ...newApplications].sort((a, b) =>
        a.name.localeCompare(b.name)
      );
    });

    if (isNotEmpty(selectedApplications)) {
      const appEnvironments =
        newApplications.find((app) => selectedApplications.includes(app.id))
          ?.environments ?? [];
      setSelectedAppEnvs(appEnvironments);
    }
  }, [data]);

  return {
    isAppsLoading,
    applications,
    selectedEnvs,
    selectedAppEnvs,
    setSelectedEnvs,
  };
}
